// src/components/chat/GlobalCatylexCitationTooltip.tsx

/**
 * GlobalCatylexCitationTooltip
 *
 * Single catylex-tooltip instance rendered via portal at root level.
 * Subscribes to CatylexCitationTooltipContext so it never unmounts during
 * streaming. Sibling of GlobalPlaybookCitationTooltip and
 * GlobalDocumentCitationTooltip.
 */

import React from "react";
import { createPortal } from "react-dom";
import type { CatylexCitationTooltipData } from "../../contexts/CatylexCitationTooltipContext";
import { useCatylexCitationTooltip } from "../../contexts/CatylexCitationTooltipContext";
import { CatylexIcon } from "./DocumentCitationIcons";
import { useTooltipPosition } from "./hooks/useTooltipPosition";
import { usePinnedTooltipDismiss } from "./hooks/usePinnedTooltipDismiss";
import { CitationTooltipPager, CitationTooltipPages } from "./CitationGroupControls";
import { catylexColors } from "../CatylexSearch/catylexSearchStyles";

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  // Snap to the last word boundary before maxLength so we never clip mid-word
  // (e.g. "shall no…" instead of "shall not…"). Falls back to a hard cut when
  // there's no space in the prefix (very long single token).
  const cut = text.lastIndexOf(" ", maxLength);
  const end = cut > 0 ? cut : maxLength;
  return text.substring(0, end) + "…";
}

/**
 * Strip leading markdown heading markers ("# ", "## ", ...) from each line.
 * BE preserves headings in chunks for the LLM; we don't want them in the tooltip.
 */
function stripHeadingMarkers(text: string): string {
  return text.replace(/^\s*#{1,6}\s+/gm, "");
}

const STATUS_STYLE: Record<"Active" | "Inactive", { bg: string; fg: string }> = {
  Active: { bg: "#E6F4EA", fg: "#1E7E34" },
  Inactive: { bg: "#ECEFF1", fg: "#546E7A" },
};

const StatusPill: React.FC<{ status: "Active" | "Inactive" }> = ({ status }) => {
  const s = STATUS_STYLE[status];
  return (
    <span
      style={{
        display: "inline-block",
        padding: "1px 6px",
        backgroundColor: s.bg,
        color: s.fg,
        borderRadius: "10px",
        fontSize: "9px",
        fontWeight: 600,
        letterSpacing: "0.02em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {status}
    </span>
  );
};

const MAX_CONCEPT_VALUES_LEN = 80;

const ConceptsList: React.FC<{ concepts: { name: string; values: string[] }[] }> = ({
  concepts,
}) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      padding: "6px 8px",
      backgroundColor: catylexColors.quoteBg,
      border: `1px solid ${catylexColors.quoteBorder}`,
      borderRadius: "6px",
    }}
  >
    {concepts.map((c, i) => {
      const joined = c.values.join(", ");
      const display = truncateText(joined, MAX_CONCEPT_VALUES_LEN);
      return (
        <div
          key={`${c.name}-${i}`}
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 110px) 1fr",
            columnGap: "8px",
            alignItems: "baseline",
            fontSize: "10px",
            lineHeight: 1.4,
          }}
        >
          <span
            style={{
              fontWeight: 600,
              color: catylexColors.primary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={c.name}
          >
            {c.name}
          </span>
          <span
            style={{
              color: catylexColors.foreground,
              wordBreak: "break-word",
            }}
            title={joined}
          >
            {display || "—"}
          </span>
        </div>
      );
    })}
  </div>
);

const CatylexTooltipTitle: React.FC<{ title: string }> = ({ title }) => (
  <div
    style={{
      fontSize: "12px",
      fontWeight: 500,
      color: "#212529",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      flex: 1,
      minWidth: 0,
    }}
    title={title}
  >
    {title}
  </div>
);

/** Breadcrumb, quote, concepts and report link for one citation. */
const CatylexTooltipBody: React.FC<{ item: CatylexCitationTooltipData }> = ({ item }) => {
  const { breadcrumb, citedText, atrUrl, contractStatus, concepts } = item;
  const cleanedCitedText = stripHeadingMarkers(citedText);
  const hasConcepts = !!concepts && concepts.length > 0;

  return (
    <>
      {/* Breadcrumb + status pill */}
      {breadcrumb.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "11px",
            color: catylexColors.primary,
            fontWeight: 500,
          }}
        >
          <span
            style={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={breadcrumb.join(" › ")}
          >
            {breadcrumb.join(" › ")}
          </span>
          {contractStatus && <StatusPill status={contractStatus} />}
        </div>
      )}

      {/* Cited text excerpt */}
      {cleanedCitedText && (
        <div
          style={{
            fontSize: "11px",
            lineHeight: 1.5,
            color: "#555",
            fontStyle: "italic",
            wordBreak: "break-word",
            borderLeft: `2px solid ${catylexColors.quoteBorder}`,
            paddingLeft: "8px",
          }}
        >
          {truncateText(cleanedCitedText, 200)}
        </div>
      )}

      {/* Concepts (contract-header chunks with enrichment only) */}
      {hasConcepts && <ConceptsList concepts={concepts!} />}

      {/* Footer: All Terms Report link */}
      {atrUrl && (
        <a
          href={atrUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: "11px",
            fontWeight: 500,
            color: catylexColors.primary,
            textDecoration: "none",
            alignSelf: "flex-start",
          }}
        >
          View All Terms Report ↗
        </a>
      )}
    </>
  );
};

export const GlobalCatylexCitationTooltip: React.FC = () => {
  const { state, handleTooltipMouseEnter, handleTooltipMouseLeave, pageTooltip, closeTooltip } =
    useCatylexCitationTooltip();
  const {
    isVisible,
    anchorRect,
    documentTitle,
    chunkType,
    breadcrumb,
    citedText,
    atrUrl,
    contractStatus,
    pages: citedPages,
    concepts,
    group,
    pinned,
  } = state;
  const { ref: tooltipRef, position } = useTooltipPosition<HTMLDivElement>(anchorRect, isVisible);

  usePinnedTooltipDismiss(isVisible && pinned, tooltipRef, group?.anchorEl ?? null, closeTooltip);

  if (!isVisible) return null;

  // A merged chip's citations, paged; null for a single citation
  const pages = group && group.items.length > 1 ? group : null;

  const tooltipContent = (
    <div
      ref={tooltipRef}
      className="catylex-citation-tooltip"
      onMouseEnter={handleTooltipMouseEnter}
      onMouseLeave={handleTooltipMouseLeave}
      role="tooltip"
      style={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 10000,
        maxWidth: "360px",
        minWidth: "260px",
        maxHeight: `${position.maxHeight}px`,
        overflowY: "auto",
        padding: "12px",
        backgroundColor: "#FFFFFF",
        border: `1px solid ${catylexColors.quoteBorder}`,
        borderRadius: "8px",
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05)",
        pointerEvents: "auto",
        fontFamily: "'Segoe UI', sans-serif",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
      }}
    >
      {/* Header: catylex icon + document title */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <CatylexIcon size={24} />
        {pages ? (
          <CitationTooltipPages index={pages.index} style={{ flex: 1, minWidth: 0 }}>
            {pages.items.map((item, i) => (
              <CatylexTooltipTitle key={i} title={item.documentTitle} />
            ))}
          </CitationTooltipPages>
        ) : (
          <CatylexTooltipTitle title={documentTitle} />
        )}
        {pages && (
          <CitationTooltipPager
            index={pages.index}
            total={pages.items.length}
            onPage={pageTooltip}
          />
        )}
      </div>

      {pages ? (
        <CitationTooltipPages index={pages.index}>
          {pages.items.map((item, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <CatylexTooltipBody item={item} />
            </div>
          ))}
        </CitationTooltipPages>
      ) : (
        <CatylexTooltipBody
          item={{
            documentTitle,
            chunkType,
            breadcrumb,
            citedText,
            atrUrl,
            contractStatus,
            pages: citedPages,
            concepts,
          }}
        />
      )}
    </div>
  );

  return createPortal(tooltipContent, document.body);
};

export default GlobalCatylexCitationTooltip;
